import {ObjectId, SceneNodes} from "@cm/template-nodes"
import {anyDifference, objectFieldsDifferent} from "@template-editor/helpers/change-detection"
import {getThreeObjectPart, mathIsEqual, setThreeObjectPart, ThreeObject, updateTransform} from "@template-editor/helpers/three-object"
import {ThreeSceneManagerService} from "@template-editor/services/three-scene-manager.service"
import {Three as THREE} from "@cm/material-nodes/three"
import {ThreeMesh} from "@app/template-editor/helpers/three-mesh"
import {projectCurvePointsToObject} from "@app/template-editor/helpers/three-utils"
import {Subscription} from "rxjs"

type InstancedMeshNode = {
    id: number
    meshDirty: boolean
    instancesDirty: boolean
    threeMesh: ThreeMesh
    subMesh: THREE.Mesh<THREE.BufferGeometry, THREE.Material>
}

export class ThreeSeam extends ThreeObject<SceneNodes.Seam> {
    protected override renderObject = new THREE.Group()

    private instancedMeshIds = new Map<string, number>()
    private instancedMeshCache = new Map<number, {instancedMesh: THREE.InstancedMesh; maxCount: number}>()
    private instances: THREE.Matrix4[] = []
    private subscription: Subscription
    private dirtyMeshes = new Set<string>()

    constructor(threeSceneManagerService: ThreeSceneManagerService, onAsyncUpdate: () => void) {
        super(threeSceneManagerService, onAsyncUpdate)

        this.subscription = threeSceneManagerService.threeObjectUpdated$.subscribe(({threeObject, asyncUpdate}) => {
            const updatedMesh = threeObject.getSceneNode()

            if (!asyncUpdate) {
                this.dirtyMeshes.add(updatedMesh.id)
                return
            }

            const sceneNode = this.getSceneNode()
            if (sceneNode.item.find((mesh) => mesh.id === updatedMesh.id) === undefined) return

            if (this.updateMeshes(sceneNode, (meshId) => meshId === updatedMesh.id, false)) this.onAsyncUpdate()
        })

        setThreeObjectPart(this.getRenderObject(), this)
    }

    protected updateMeshes(sceneNode: SceneNodes.Seam, meshDirty: (meshId: ObjectId) => boolean, instancesDirty: boolean) {
        const instancedMeshData: InstancedMeshNode[] = []
        for (const mesh of sceneNode.item) {
            const meshObject = this.threeSceneManagerService.getThreeObject(mesh.id)
            if (!meshObject || !(meshObject instanceof ThreeMesh)) throw new Error(`Mesh object with id ${mesh.id} not found`)

            const threeMesh = meshObject

            for (const {subMesh, materialIndex} of threeMesh.getSubMeshes()) {
                instancedMeshData.push({
                    id: this.getInstancedMeshId(mesh, materialIndex),
                    meshDirty: meshDirty(mesh.id),
                    instancesDirty,
                    threeMesh,
                    subMesh,
                })
            }
        }

        return this.updateInstancedMeshes(instancedMeshData)
    }

    protected getInstancedMeshId(mesh: SceneNodes.Mesh, materialIndex: number) {
        const key = `${mesh.id}@${materialIndex}`
        const existing = this.instancedMeshIds.get(key)
        if (existing !== undefined) return existing
        else {
            const id = this.instancedMeshIds.size
            this.instancedMeshIds.set(key, id)
            return id
        }
    }

    protected updateInstancedMeshes(meshNodes: InstancedMeshNode[]) {
        const toDelete = new Set<number>()
        for (const [id] of this.instancedMeshCache) toDelete.add(id)

        let needsUpdate = false

        const getInstancedMesh = (meshNode: InstancedMeshNode) => {
            const {id, subMesh, meshDirty} = meshNode
            const {geometry, material, visible, receiveShadow, castShadow, renderOrder} = subMesh

            const cachedInstancedMesh = this.instancedMeshCache.get(id)
            if (cachedInstancedMesh) {
                const {instancedMesh, maxCount} = cachedInstancedMesh
                if (meshDirty || maxCount < this.instances.length) {
                    this.renderObject.remove(instancedMesh)
                    this.instancedMeshCache.delete(id)
                    instancedMesh.dispose()
                } else return instancedMesh
            }

            const maxCount = Math.max(this.instances.length * 2, 10)
            const instancedMesh = new THREE.InstancedMesh(geometry, material, maxCount)
            setThreeObjectPart(instancedMesh, this, `group${id}`)
            instancedMesh.visible = visible
            instancedMesh.receiveShadow = receiveShadow
            instancedMesh.castShadow = castShadow
            instancedMesh.renderOrder = renderOrder

            this.renderObject.add(instancedMesh)
            this.instancedMeshCache.set(id, {
                instancedMesh,
                maxCount,
            })
            return instancedMesh
        }

        for (const meshNode of meshNodes) {
            const instancedMesh = getInstancedMesh(meshNode)

            const {instancesDirty} = meshNode

            if (instancesDirty || instancedMesh.count !== this.instances.length) {
                const {threeMesh} = meshNode
                const {matrix} = threeMesh.getRenderObject()

                let curId = 0
                for (let i = 0; i < this.instances.length; i++) {
                    instancedMesh.setMatrixAt(curId, matrix.clone().premultiply(this.instances[i]))
                    curId++
                }

                instancedMesh.instanceMatrix.needsUpdate = true

                instancedMesh.count = curId

                needsUpdate = true
            }

            toDelete.delete(meshNode.id)
        }

        for (const id of toDelete) {
            const cachedInstancedMesh = this.instancedMeshCache.get(id)
            if (cachedInstancedMesh) {
                const {instancedMesh} = cachedInstancedMesh
                this.renderObject.remove(instancedMesh)
                this.instancedMeshCache.delete(id)
                instancedMesh.dispose()
                needsUpdate = true
            }
        }

        return needsUpdate
    }

    override setup(sceneNode: SceneNodes.Seam) {
        let instancesDirty = false

        return anyDifference([
            objectFieldsDifferent(
                sceneNode,
                this.parameters,
                ["transform"],
                (valueA, valueB) => mathIsEqual(valueA, valueB),
                ({transform}) => {
                    updateTransform(transform, this.renderObject)
                },
            ),
            objectFieldsDifferent(
                sceneNode,
                this.parameters,
                ["completeCurveData", "meshCurveControl"],
                (a, b) => {
                    if (a === null || b === null) return a === b
                    else if (SceneNodes.isSceneNode(a)) {
                        if (SceneNodes.isSceneNode(b)) return a.mesh.id === b.mesh.id
                        return false
                    } else if (SceneNodes.isSceneNode(b)) return false
                    return a.reified?.points === b.reified?.points && a.reified?.normals === b.reified?.normals && a.reified?.tangents === b.reified?.tangents
                },
                ({completeCurveData, meshCurveControl}) => {
                    const threeMeshObject = this.threeSceneManagerService.getThreeObject(meshCurveControl.mesh.id)
                    if (!threeMeshObject || !(threeMeshObject instanceof ThreeMesh))
                        throw new Error(`Mesh object with id ${meshCurveControl.mesh.id} not found`)
                    const meshScene = threeMeshObject.getRenderObject()
                    //We need to update the world matrix of the mesh to get the correct hit points even before the first render
                    meshScene.updateMatrixWorld()

                    const segmentLength = 0.1
                    const queryRange = 2 * segmentLength

                    this.instances.length = 0
                    if (completeCurveData.reified) {
                        const {hitPoints, hitNormals, valid} = projectCurvePointsToObject(
                            completeCurveData.reified,
                            this.renderObject.matrix,
                            queryRange,
                            meshScene,
                        )
                        const {scales, tangents} = completeCurveData.reified

                        for (let i = 0; i < valid.length; i++) {
                            if (!valid[i]) continue

                            const position = hitPoints[i]
                            const normal = hitNormals[i]
                            const tangent = new THREE.Vector3(tangents[i * 3], tangents[i * 3 + 1], tangents[i * 3 + 2]).projectOnPlane(normal).normalize()
                            const bitangent = new THREE.Vector3().crossVectors(normal, tangent).normalize()

                            const matrix = new THREE.Matrix4()
                                .makeBasis(bitangent, normal, tangent)
                                .setPosition(position)
                                .scale(new THREE.Vector3(1, 1, scales[i]))

                            this.instances.push(matrix)
                        }
                    }

                    instancesDirty = true
                },
            ),
            (() => {
                const needsUpdate = this.updateMeshes(sceneNode, (meshId) => this.dirtyMeshes.has(meshId), instancesDirty)
                this.dirtyMeshes.clear()
                return needsUpdate
            })(),
        ])
    }

    getSubMesh(index: number) {
        for (const child of this.renderObject.children) {
            if (child instanceof THREE.InstancedMesh) {
                const threeObjectPart = getThreeObjectPart(child)

                if (threeObjectPart && threeObjectPart.part === `group${index}`) return child as THREE.InstancedMesh<THREE.BufferGeometry, THREE.Material>
            }
        }

        throw new Error(`Control point with index ${index} not found`)
    }

    override dispose(_final: boolean) {
        for (const {instancedMesh} of this.instancedMeshCache.values()) {
            this.renderObject.remove(instancedMesh)
            instancedMesh.dispose()
        }

        this.instancedMeshCache.clear()
        this.instancedMeshIds.clear()
        this.instances.length = 0

        this.subscription.unsubscribe()
    }

    override onSelectionChange(_selected: boolean) {}
}
